import { useEffect, useEffectEvent, useState } from 'react';

/** Distance of the indicator from which letting go refreshes, in pixels. */
export const PULL_THRESHOLD = 64;
const PULL_MAX = 96;
/** The indicator moves half as far as the finger, so the pull feels like it has weight. */
const RESISTANCE = 0.5;
/** Finger movement before the direction of the gesture counts. */
const SLOP = 8;

/**
 * Places where a downward swipe belongs to something else: open dialogs, sheets and popups
 * (Radix locks the page scroll and sets `data-scroll-locked`), maps.
 */
const OWN_GESTURES = '[role="dialog"], [data-radix-popper-content-wrapper], .maplibregl-map';

function startsElsewhere(target: EventTarget | null): boolean {
  if (document.body.hasAttribute('data-scroll-locked')) return true;
  if (!(target instanceof Element)) return true;
  if (target.closest(OWN_GESTURES)) return true;
  for (
    let node: Element | null = target;
    node && node !== document.body;
    node = node.parentElement
  ) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return true;
    }
  }
  return false;
}

export interface PullState {
  /** How far the indicator has come down, in pixels. */
  distance: number;
  refreshing: boolean;
}

/**
 * Pull to refresh on touch screens (docs/ui/responsive.md, Gesten): pulling down at the top of the
 * page and letting go past `PULL_THRESHOLD` calls `onRefresh`. Only when the page is scrolled to
 * the top and the touch does not start in an overlay, a map or a container that scrolls itself.
 * Listeners are passive, so scrolling never waits for them. While mounted the browser's own pull
 * to refresh is off, so it cannot reload the page on top.
 */
export function usePullToRefresh(onRefresh: () => Promise<void>): PullState {
  const [state, setState] = useState<PullState>({ distance: 0, refreshing: false });
  const refresh = useEffectEvent(onRefresh);

  useEffect(() => {
    const root = document.documentElement;
    const overscroll = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = 'contain';

    let start: { x: number; y: number } | null = null;
    let pulling = false;
    let distance = 0;
    let refreshing = false;

    const reset = () => {
      start = null;
      pulling = false;
      distance = 0;
    };

    const onStart = (event: TouchEvent) => {
      reset();
      const touch = event.touches[0];
      if (refreshing || !touch || event.touches.length > 1 || window.scrollY > 0) return;
      if (startsElsewhere(event.target)) return;
      start = { x: touch.clientX, y: touch.clientY };
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (!pulling) {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        // Sideways (tables, carousels) or up (scrolling the page) is no pull.
        if (dy <= 0 || Math.abs(dx) > dy || window.scrollY > 0) {
          start = null;
          return;
        }
        pulling = true;
      }
      distance = Math.min(Math.max(dy, 0) * RESISTANCE, PULL_MAX);
      setState({ distance, refreshing: false });
    };

    const onEnd = () => {
      const release = pulling && distance >= PULL_THRESHOLD;
      const pulled = pulling;
      reset();
      if (!pulled) return;
      if (!release) {
        setState({ distance: 0, refreshing: false });
        return;
      }
      refreshing = true;
      setState({ distance: 0, refreshing: true });
      void refresh().finally(() => {
        refreshing = false;
        setState({ distance: 0, refreshing: false });
      });
    };

    const onCancel = () => {
      if (pulling) setState({ distance: 0, refreshing: false });
      reset();
    };

    const passive = { passive: true } as const;
    document.addEventListener('touchstart', onStart, passive);
    document.addEventListener('touchmove', onMove, passive);
    document.addEventListener('touchend', onEnd, passive);
    document.addEventListener('touchcancel', onCancel, passive);
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
      root.style.overscrollBehaviorY = overscroll;
    };
  }, []);

  return state;
}
